import { useSyncExternalStore } from 'react'

/**
 * First-backtest coach: a one-time spotlight shown when the user's very first
 * backtest completes —
 *   'card'   → ring the results badge in chat (its actions stay disabled
 *              until the user acknowledges the tip),
 *   'done'   → finished; never shows again (localStorage flag, written only
 *              when the sequence completes — an abandoned run re-arms).
 * (A second step used to ring the workbench toolbar's "Overlay backtest"
 * checkbox; that overlay is gone — the canvas shows the walkthrough instead.)
 * Module-level store + useSyncExternalStore (the chartPush bridge pattern):
 * the badge, the workbench toolbar and BotDetail all consume the same
 * sequencing state from separate subtrees.
 */

const SEEN_KEY = 'algobuilder.backtestCoachSeen'

export const coachStorage = {
  isDone(): boolean {
    try {
      return localStorage.getItem(SEEN_KEY) === '1'
    } catch {
      return false
    }
  },
  markDone(): void {
    try {
      localStorage.setItem(SEEN_KEY, '1')
    } catch {
      // storage unavailable — the coach may show again next time, harmless
    }
  },
  reset(): void {
    try {
      localStorage.removeItem(SEEN_KEY)
    } catch {
      // ignore
    }
  },
}

export type CoachStep = 'none' | 'card' | 'done'

export interface BacktestCoachState {
  step: CoachStep
  /** Chat msgId of the completed-backtest message the sequence anchors to. */
  msgId: string | null
}

let state: BacktestCoachState = { step: 'none', msgId: null }
const listeners = new Set<() => void>()

function set(next: BacktestCoachState) {
  state = next
  listeners.forEach((l) => l())
}

export const backtestCoachStore = {
  get: (): BacktestCoachState => state,

  /** Begin the sequence when a backtest completes (no-op if seen or active). */
  start(msgId: string): void {
    if (state.step !== 'none' || coachStorage.isDone()) return
    set({ step: 'card', msgId })
  },

  /** Advance: card → done (persists the seen flag). */
  next(): void {
    if (state.step === 'card') {
      coachStorage.markDone()
      set({ ...state, step: 'done' })
    }
  },

  /** End without re-writing the flag (another tab completed the sequence). */
  finishSilently(): void {
    if (state.step === 'card') {
      set({ ...state, step: 'done' })
    }
  },

  /** Bot switch — abandon an in-flight sequence without marking it seen. */
  reset(): void {
    if (state.step !== 'none' || state.msgId !== null) {
      set({ step: 'none', msgId: null })
    }
  },

  subscribe(l: () => void): () => void {
    listeners.add(l)
    return () => listeners.delete(l)
  },
}

// A second open tab completing the sequence flips the flag — end this tab's
// coach too instead of leaving a stale spotlight up.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === SEEN_KEY && e.newValue === '1') backtestCoachStore.finishSilently()
  })
}

export function useBacktestCoach(): BacktestCoachState {
  return useSyncExternalStore(backtestCoachStore.subscribe, backtestCoachStore.get, backtestCoachStore.get)
}
